/**
 * report/rules.ts — de regeltaal van de keuzeteksten (featureplan §5). Geen eval: een regel is
 * JSON en wordt hier gelezen, niet uitgevoerd.
 *
 *   { "fact": "asbestosSoil.found", "eq": true }
 *   { "fact": "asbestos.maxValueMgKg", "lt": 100 }
 *   { "any": [ ... ] }   minstens één
 *   { "all": [ ... ] }   allemaal
 *   { "not": { ... } }   niet
 *
 * Vergelijkingen: eq, ne, gt, gte, lt, lte, in (lijst), exists (true/false).
 *
 * Elke toets geeft ook terug WAAROM: welke feiten met welke waarde. Daarmee schrijft de editor
 * 'Gekozen omdat: asbest aangetroffen in bodem (ASB01, 15 mg/kg d.s.)' en bij een niet-passende
 * variant waarom niet.
 */

import type { ReportFactValue } from './types.js';

export type Facts = Record<string, ReportFactValue | ReportFactValue[] | undefined>;

export type Rule =
  | { fact: string; eq?: ReportFactValue; ne?: ReportFactValue; gt?: number; gte?: number; lt?: number; lte?: number; in?: ReportFactValue[]; exists?: boolean }
  | { any: Rule[] }
  | { all: Rule[] }
  | { not: Rule };

export interface RuleCheck {
  fact: string;
  /** De waarde in de feiten (undefined = niet bekend). */
  value: ReportFactValue | ReportFactValue[] | undefined;
  /** De vergelijking als leesbare tekst: '= ja', '< 100'. */
  expected: string;
  ok: boolean;
}

export interface RuleResult {
  match: boolean;
  /** De toetsen die de uitkomst bepaalden, in leesvolgorde. */
  checks: RuleCheck[];
}

const OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'exists'] as const;
const show = (v: unknown) => (v === true ? 'ja' : v === false ? 'nee' : v == null ? 'onbekend' : Array.isArray(v) ? v.join(', ') : String(v));
const SYMBOL: Record<string, string> = { eq: '=', ne: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', in: 'in', exists: 'bekend' };

function compare(op: (typeof OPS)[number], value: unknown, target: unknown): boolean {
  const num = (x: unknown) => (typeof x === 'number' ? x : Number(String(x ?? '').replace(',', '.')));
  switch (op) {
    // Een onbekend feit telt als 'nee' bij een ja/nee-vraag: geen afwijking vastgelegd = geen afwijking.
    case 'eq': return value === target || (value == null && target === false);
    case 'ne': return !(value === target);
    case 'gt': return value != null && num(value) > num(target);
    case 'gte': return value != null && num(value) >= num(target);
    case 'lt': return value != null && num(value) < num(target);
    case 'lte': return value != null && num(value) <= num(target);
    case 'in': return Array.isArray(target) && target.includes(value as ReportFactValue);
    case 'exists': return (value != null && value !== '') === !!target;
  }
}

/** Is de regel geldig van vorm? Gebruikt bij het opslaan van een bureautekst. */
export function validateRule(rule: unknown, depth = 0): string | null {
  if (depth > 6) return 'De regel is te diep genest';
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return 'Een regel is een object';
  const r = rule as Record<string, unknown>;
  if ('any' in r || 'all' in r) {
    const list = (r.any ?? r.all) as unknown;
    if (!Array.isArray(list) || !list.length) return "'any' en 'all' hebben een niet-lege lijst nodig";
    for (const x of list) { const e = validateRule(x, depth + 1); if (e) return e; }
    return null;
  }
  if ('not' in r) return validateRule(r.not, depth + 1);
  if (typeof r.fact !== 'string' || !r.fact) return "Een voorwaarde heeft een 'fact' nodig";
  const ops = OPS.filter((o) => o in r);
  if (ops.length !== 1) return `Een voorwaarde heeft precies één vergelijking: ${OPS.join(', ')}`;
  return null;
}

/** Toets een regel op de feiten. Een ontbrekende of ongeldige regel past altijd (vaste tekst). */
export function evaluateRule(rule: Rule | null | undefined, facts: Facts): RuleResult {
  if (!rule) return { match: true, checks: [] };
  if ('all' in rule) {
    const parts = rule.all.map((r) => evaluateRule(r, facts));
    return { match: parts.every((p) => p.match), checks: parts.flatMap((p) => p.checks) };
  }
  if ('any' in rule) {
    const parts = rule.any.map((r) => evaluateRule(r, facts));
    const hit = parts.filter((p) => p.match);
    // Past hij: de reden zijn de toetsen die pasten. Past hij niet: alle toetsen (waarom niet).
    return { match: hit.length > 0, checks: (hit.length ? hit : parts).flatMap((p) => p.checks) };
  }
  if ('not' in rule) {
    const inner = evaluateRule(rule.not, facts);
    return { match: !inner.match, checks: inner.checks.map((c) => ({ ...c, expected: `niet ${c.expected}`, ok: !c.ok })) };
  }
  const op = OPS.find((o) => o in rule)!;
  const target = (rule as Record<string, unknown>)[op];
  const value = facts[rule.fact];
  const ok = compare(op, value, target);
  return { match: ok, checks: [{ fact: rule.fact, value, expected: `${SYMBOL[op]} ${show(target)}`, ok }] };
}

/**
 * Vul {{feit}} in een tekst met de waarde uit de feiten. Een onbekend feit wordt
 * '[feit onbekend]', zodat het in de tekst opvalt en niet stil leeg blijft.
 */
export function interpolate(text: string, facts: Facts): string {
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) => {
    const v = facts[key];
    return v == null || v === '' ? `[${key} onbekend]` : show(v);
  });
}

export { show as showFactValue };
